import { Snackbar, Alert } from '@mui/material';
import type { SnackbarOrigin } from '@mui/material/Snackbar';
import React, { createContext, useCallback, useContext, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ReactComponent as WarningIcon } from '../assets/images/WarningIcon.svg';

type ToastOptions = {
    origin: SnackbarOrigin;
};

type ToastSuccess = (message: string, options?: ToastOptions) => void;
type ToastError = (message: string, options?: ToastOptions) => void;

type ToastContextValue = {
    success: ToastSuccess;
    error: ToastError;
};

export const ToastContext = createContext<ToastContextValue>({
    success: () => {},
    error: () => {},
});

type ToastMessage = {
    id: string;
    type: 'success' | 'error';
    message: string;
    options: ToastOptions;
    open: boolean;
};

const defaultOptions: ToastOptions = {
    origin: {
        horizontal: 'center',
        vertical: 'top',
    },
};

export const ToastProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const [messages, setMessages] = useState<ToastMessage[]>([]);

    const success: ToastSuccess = useCallback(
        (message, options = defaultOptions) => {
            const newMessage: ToastMessage = {
                id: Date.now().toString(),
                type: 'success',
                message,
                options,
                open: true,
            };
            setMessages((prevMessages) => [...prevMessages, newMessage]);
        },
        [messages],
    );

    const error: ToastError = useCallback(
        (message, options = defaultOptions) => {
            const newMessage: ToastMessage = {
                id: Date.now().toString(),
                type: 'error',
                message,
                options,
                open: true,
            };
            setMessages((prevMessages) => [...prevMessages, newMessage]);
        },
        [messages],
    );

    const onClose = (messageId: string) => {
        const filteredMessages = messages.filter((message) => message.id !== messageId);
        setMessages([...filteredMessages]);
    };

    return (
        <ToastContext.Provider value={{ success, error }}>
            {children}
            {messages.map((message) => (
                <Snackbar
                    key={message.id}
                    open={message.open}
                    onClose={() => onClose(message.id)}
                    anchorOrigin={message.options.origin}
                    slotProps={{
                        root: {
                            style: {},
                        },
                    }}
                >
                    <Alert
                        variant="filled"
                        sx={{
                            borderRadius: '12px',
                            backgroundColor: message.type === 'success' ? '#4caf50' : '#f44336',
                        }}
                        severity={message.type}
                        icon={
                            message.type === 'success' ? (
                                <CheckCircleIcon sx={{ marginRight: '2px' }} />
                            ) : (
                                <WarningIcon style={{ marginRight: '2px' }} />
                            )
                        }
                        onClose={() => onClose(message.id)}
                    >
                        {message.message}
                    </Alert>
                </Snackbar>
            ))}
        </ToastContext.Provider>
    );
};

export const ToastConsumer = ToastContext.Consumer;

export const useToast = () => useContext(ToastContext);
